import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { maxLength } from '../../../../utils/validate'
import { useTranslation } from 'react-i18next'
import { replaceAndStrip } from '../../../../utils/string'
import Modal from '../../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AddTeam,
  fetchTeamList,
  UpdateTeam,
} from '../../../../states/containers/team/listSlice'
import { updateCommonPart } from '../../../../states/containers/common/commonPartSlice'
import AddMemberModule from './AddMemberModule'
import Tips from '../../common/Tips'

interface SimpleMember {
  id: number
  name: string
  email: string
  profile_image: string | null
  contract: string
  place: string
  active: boolean
}

interface SimpleTeam {
  team_id: number
  name: string
  code: string | null
  count: number
  created_at: string
  members: SimpleMember[]
  managers: SimpleMember[]
}

interface TeamModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetTeamData: SimpleTeam | null
}

interface Errors {
  name: string[],
  member: string[],
}

interface Props {
  teamModalState: TeamModalState
  closeCallback: VoidFunction
  addCallback?: VoidFunction
  modCallback?: VoidFunction
  closeable?: boolean
}

const TeamModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    teamList,
  } = useSelector((state: RootState) => state)

  const {
    teamModalState,
    closeCallback,
    addCallback,
    modCallback,
    closeable,
  } = props

  const initialInput = {
    name: "",
    code: "",
  }

  const initErrors = {
    name: [],
    member: [],
  }

  const [teamName, setTeamName] = useState<string>("")
  const [teamCode, setTeamCode] = useState<string>("")

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>(initErrors)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const [selectMembers, setSelectMembers] = useState<SimpleMember[]>([])
  const [selectManagers, setSelectManagers] = useState<SimpleMember[]>([])

  const clear = () => {
    closeCallback()
    setTimeout(() => {
      setTeamName("")
      setSelectMembers([])
    }, 500)
  }

  const replaceAndStripName = (event: React.FocusEvent<HTMLInputElement>) => {
    setTeamName(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        name: [],
      })
    }
  }

  const replaceAndStripCode = (event: React.FocusEvent<HTMLInputElement>) => {
    setTeamCode(replaceAndStrip(event.target.value))
  }

  const setMember = (members: SimpleMember[]) => {
    setSelectMembers(members)
    if (members.length) {
      setErrors({
        ...errors,
        member: [],
      })
    }
  }

  const saveTeam = () => {
    if (!commonPart.company?.id) return
    setIsSubmit(true)
    const tmpErrors: Errors = initErrors
    let errorFlg = false

    // name
    if (teamName === '') {
      tmpErrors.name.push(t('form.error.no_team_name_error'))
      errorFlg = true
    }
    const nameLength = maxLength(30)(teamName)
    if (nameLength) {
      tmpErrors.name.push(t(nameLength, { length: 30 }))
      errorFlg = true
    }
    const teamNameList = teamModalState.type === "create"
      ? teamList.teams.map((item) => item.name)
      : teamList.teams.filter((v) => v.name !== teamModalState.targetTeamData?.name).map((item) => item.name)
    if (teamNameList.indexOf(teamName) !== -1) {
      tmpErrors.name.push(t('form.error.team_name_duplicate_error'))
      errorFlg = true
    }
    setErrors({
      name: tmpErrors.name,
      member: tmpErrors.member,
    })
    if (errorFlg) return
    if (teamModalState.type === "create") {
      dispatch(
        AddTeam(
          {
            name: teamName,
            code: teamCode,
            manager_ids: selectManagers.map((v) => v.id),
            member_ids: selectMembers.map((v) => v.id),
          },
          () => {
            alert.success(t('setting.team.alert.create_team', { name: teamName }))
            clear()
            dispatch(updateCommonPart())
            dispatch(fetchTeamList(
              () => {
                setIsSubmit(false)
                if (addCallback) addCallback()
              },
              false,
            ))
          },
          (error) => {
            error.forEach((e: { column_name: string, message: string[] }) => {
              if (e.message[0] === '.api.v1.features.setting.team_controller.team_name.duplicate') {
                const el = document.getElementById('team_name_error')
                if (el) el.textContent = t('form.error.team_name_duplicate_error')
              }
            })
          },
        ),
      )
    } else if (teamModalState.type === "update" && teamModalState.id) {
      let tmpValues:{ name?: string; code?: string; manager_ids: number[]; member_ids: number[]; } = {
        manager_ids: selectManagers.map((v) => v.id),
        member_ids: selectMembers.map((v) => v.id),
      }
      if ((teamModalState.targetTeamData?.name || initialInput.name) !== teamName) {
        tmpValues = {
          ...tmpValues,
          name: teamName,
        }
      }
      if ((teamModalState.targetTeamData?.code || initialInput.code) !== teamCode) {
        tmpValues = {
          ...tmpValues,
          code: teamCode,
        }
      }
      dispatch(
        UpdateTeam(
          teamModalState.id,
          tmpValues,
          () => {
            alert.success(`チーム:${teamName}を変更しました`)
            clear()
            dispatch(updateCommonPart())
            dispatch(fetchTeamList(
              () => {
                setIsSubmit(false)
                if (modCallback) modCallback()
              },
              false,
            ))
          },
          (error) => {
            error.forEach((e: { column_name: string, message: string[] }) => {
              if (e.message[0] === ".api.v1.features.setting.team_controller.after_team_name.duplicate") {
                const el = document.getElementById('no_after_team_name_error')
                if (el) el.textContent = t('form.error.team_name_duplicate_error')
              }
            })
          },
        ),
      )
    }
  }

  useEffect(() => {
    if (!teamModalState) return
    const { targetTeamData } = teamModalState
    setTeamName(targetTeamData?.name || initialInput.name)
    setTeamCode(targetTeamData?.code || initialInput.code)
    setSelectMembers(targetTeamData?.members || [])
    setSelectManagers(targetTeamData?.managers || [])
    setErrors(initErrors)
  }, [teamModalState])

  useEffect(() => {
    setDisplayModal(teamModalState.display)
    setIsSubmit(false)
  }, [teamModalState.display])

  return (
    <Modal
      title={teamModalState.type === "create" ? "チームの作成" : "チームの編集"}
      show={displayModal}
      wrapperClass="setting_member"
      closeCallback={clear}
      closeable={closeable}
    >
      <>
        <div className="item">
          <div className="title flex">
            <div className="label red">必須</div>
            {t('setting.team_modal.update_name.name_label')}
          </div>
          <div className="detail row_1">
            <input
              type="text"
              placeholder={t('model.member.name')}
              onChange={(event) => setTeamName(event.target.value)}
              onBlur={(event) => replaceAndStripName(event)}
              value={teamName}
              style={{
                height: "auto",
                padding: "16px",
                backgroundColor: "#FFFFFF",
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          {
            errors.name && errors.name.map((error) => (
              <div className="error" key={error}>
                {error}
              </div>
            ))
          }
        </div>
        <div className="item">
          <div className="title flex">
            <div className="label green">任意</div>
            部門コード
          </div>
          <div className="detail row_1">
            <input
              type="text"
              placeholder="チームの識別など"
              onChange={(event) => setTeamCode(event.target.value)}
              onBlur={(event) => replaceAndStripCode(event)}
              value={teamCode}
              style={{
                height: "auto",
                padding: "16px",
                backgroundColor: "#FFFFFF",
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
        <div className="item">
          <div className="title">
            マネージャー
          </div>
          <fieldset
            style={{
              padding: "16px",
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              {
                selectManagers.map((value) => (
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "20px",
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    key={`condition_${value.id}`}
                  >
                    {
                      value.profile_image ? (
                        <img
                          alt="profile"
                          src={value.profile_image}
                          className="profile_icon"
                          style={{
                            borderRadius: "50%",
                            width: "20px",
                          }}
                        />
                      ) : (
                        <img
                          alt=""
                          src={`/static/images/profile/${(Number(value.id) || 0) % 12}.jpeg`}
                          className="profile_icon"
                          style={{
                            borderRadius: "50%",
                            width: "20px",
                          }}
                        />
                      )
                    }
                    {value.name}
                    {
                      value.active || (
                        <Tips
                          description="休眠中💤"
                          className=""
                          icon={false}
                        >
                          <>
                            💤
                          </>
                        </Tips>
                      )
                    }
                    <span
                      style={{
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectManagers([...selectManagers.filter((v) => v.id !== value.id)])}
                      role="none"
                    >
                      <FontAwesomeIcon icon={['fas', 'times']} />
                    </span>
                  </div>
                ))
              }
              <AddMemberModule
                memberIdList={selectManagers.map((v) => v.id)}
                callbackMembers={(val) => setSelectManagers([...teamList.full_members.filter((v) => val.map((w) => w.id).includes(v.id))])}
                memberList={teamList.full_members.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))}
              />
            </div>
          </fieldset>
        </div>
        <div className="item">
          <div className="title">
            メンバー
          </div>
          <fieldset
            style={{
              padding: "16px",
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              {
                selectMembers.map((value) => (
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "20px",
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    key={`condition_${value.id}`}
                  >
                    {
                      value.profile_image ? (
                        <img
                          alt="profile"
                          src={value.profile_image}
                          className="profile_icon"
                          style={{
                            borderRadius: "50%",
                            width: "20px",
                          }}
                        />
                      ) : (
                        <img
                          alt=""
                          src={`/static/images/profile/${(Number(value.id) || 0) % 12}.jpeg`}
                          className="profile_icon"
                          style={{
                            borderRadius: "50%",
                            width: "20px",
                          }}
                        />
                      )
                    }
                    {value.name}
                    {
                      value.active || (
                        <Tips
                          description="休眠中💤"
                          className=""
                          icon={false}
                        >
                          <>
                            💤
                          </>
                        </Tips>
                      )
                    }
                    <span
                      style={{
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectMembers([...selectMembers.filter((v) => v.id !== value.id)])}
                      role="none"
                    >
                      <FontAwesomeIcon icon={['fas', 'times']} />
                    </span>
                  </div>
                ))
              }
              <AddMemberModule
                memberIdList={selectMembers.map((v) => v.id)}
                callbackMembers={(val) => setMember([...teamList.full_members.filter((v) => val.map((w) => w.id).includes(v.id))])}
                memberList={teamList.full_members.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))}
              />
            </div>
          </fieldset>
        </div>
      </>
      <>
        <button type="button" className="button cancel" onClick={clear} disabled={!closeable}>
          {t('form.component.cancel')}
        </button>
        <button
          type="button"
          className="button"
          onClick={saveTeam}
          disabled={!!errors.name.length || isSubmit}
        >
          { teamModalState.type === 'create' ? t('form.component.create') : t('form.component.modify') }
        </button>
      </>
    </Modal>
  )
}

TeamModal.defaultProps = {
  addCallback: undefined,
  modCallback: undefined,
  closeable: undefined,
}

export default TeamModal
